import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';

import { SessionManager } from './session-manager';
import { ApiBaseService } from './api-base.service';

import { GiviScanSessionModel, SessionModel } from '@models/SessionModel';
import { LoginModel } from '@models/LoginModel';
import { CustomerModel, NewCustomerAccessTokenModel } from '@models/CustomerModel';
import { SharedService } from './shared.service';
import { Country } from '@models/Enums';


@Injectable()
export class ApiAuthService extends ApiBaseService {

    constructor(public http: HttpClient, public sharedService: SharedService) {
        super(http, sharedService);
    }


    // GET api/admin/meta/countries/reads/v1 — países habilitados (EnabledCountries). Anónimo y cacheado.
    private static enabledCountries: number[] = null;
    getEnabledCountries(): Promise<number[]> {

        if (ApiAuthService.enabledCountries) return Promise.resolve(ApiAuthService.enabledCountries);

        return this.http.get(`${this.baseUrl}/api/admin/meta/countries/reads/v1`)
            .toPromise().then(
                (result: any) => {
                    ApiAuthService.enabledCountries = result as number[];
                    return ApiAuthService.enabledCountries;
                },
                () => [1, 2] // Fallback CR, PA si el API no responde
            );
    }

    reloadSessionData(): Promise<SessionModel> {

        // load user data because session user has no company
        return this.loadAccount().then(
            () => {

                const newSession = this.getSession();
                return newSession;
            },
            (err: any) => {

                const errMsg = this.getErrorMsg(err);

                return Promise.reject(new Error(errMsg));
            }
        );
    }

    loginGuest() {

        return this.login('guest', 'GuestGiviWeb', true);
    }

    // POST Auth
    login(username: string, password: string, remember = false, clientId = 'giviappemail'): Promise<SessionModel> {

        this.setSession(null);
        SessionManager.clearSession();

        const httpOpts = {
            headers: new HttpHeaders({
                'Cache-Control': 'no-cache',
                'Content-Type': 'application/x-www-form-urlencoded',
                'Access-Control-Allow-Headers': 'Content-Type',
                'Access-Control-Allow-Methods': 'GET,POST,UPDATE,DELETE,OPTIONS',
                'Access-Control-Allow-Origin': '*',
                'X-TESTMODE': SessionManager.isDemoMode() ? '1' : ''
            })
        };

        let userName = username;
        let passComplete = password;

        if (username !== 'guest' && password !== 'GuestGiviWeb') {
            userName = encodeURIComponent(username);
            passComplete = encodeURIComponent(password);
        }

        const credentials = `grant_type=password&username=${userName}&password=${passComplete}&clientid=${clientId}`;

        return this.http.post(`${this.baseUrl}/token`, credentials, httpOpts)
            .toPromise().then(
                (response: any) => {

                    const session = new SessionModel({
                        accessToken: response.access_token,
                        refreshToken: response.refresh_token,
                        user: new CustomerModel(response)
                    });

                    const loggedIn = (session.accessToken != null);
                    if (loggedIn) {

                        if (remember) {

                            const loginModel = new LoginModel({ username, password, remember });
                            // store the credential to be remembered
                            SessionManager.setCredentials(loginModel);
                        } else {

                            SessionManager.clearCredentials();
                        }
                    }

                    SessionManager.setSession(session);

                    return this.reloadSessionData();
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(errMsg));
                }
            );
    }

    // POST Auth - grant `givi-google`: el password es el idToken de Google.
    // Separado de login() porque ahí un remember guardaría el idToken como
    // credencial, y ese token expira en aproximadamente una hora.
    // `country` viaja en X-COUNTRY: el API lo guarda en la cuenta que crea este grant
    // (antes quedaba en 0) y corrige cuentas viejas que quedaron sin país.
    loginWithGoogle(email: string, idToken: string, country?: Country): Promise<SessionModel> {

        this.setSession(null);
        SessionManager.clearSession();

        const httpOpts = { headers: new HttpHeaders(this._tokenHeaders(country)) };

        const credentials = `grant_type=password&username=${encodeURIComponent(email)}`
            + `&password=${encodeURIComponent(idToken)}&clientid=givi-google`;

        return this.http.post(`${this.baseUrl}/token`, credentials, httpOpts)
            .toPromise().then(
                (response: any) => {

                    const session = new SessionModel({
                        accessToken: response.access_token,
                        refreshToken: response.refresh_token,
                        user: new CustomerModel(response)
                    });

                    if (!session.accessToken) {

                        return Promise.reject(new Error('No se pudo iniciar sesión con Google.'));
                    }

                    SessionManager.setSession(session);

                    return session;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(errMsg));
                }
            );
    }

    // POST Auth
    validateLogin(username: string, password: string, clientId = 'giviappemail', country?: Country): Promise<SessionModel> {

        const httpOpts = { headers: new HttpHeaders(this._tokenHeaders(country)) };

        const userName = encodeURIComponent(username);
        const passComplete = encodeURIComponent(password);

        const credentials = `grant_type=password&username=${userName}&password=${passComplete}&clientId=${clientId}`;

        return this.http.post(`${this.baseUrl}/token`, credentials, httpOpts)
            .toPromise().then(
                (response: any) => {

                    let session: SessionModel = null;
                    if (response.access_token) {
                        session = new SessionModel({
                            accessToken: response.access_token,
                            refreshToken: response.refresh_token,
                            user: new CustomerModel(response)
                        });
                    }

                    return session;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(errMsg));
                }
            );
    }

    // POST Auth
    giviScanLogin(username: string, password: string, remember: boolean = false, customerId: number): Promise<GiviScanSessionModel> {

        SessionManager.clearGiviScanSession();

        const httpOpts = {
            headers: new HttpHeaders({
                'Cache-Control': 'no-cache',
                'Content-Type': 'application/x-www-form-urlencoded',
                'Access-Control-Allow-Headers': 'Content-Type',
                'Access-Control-Allow-Methods': 'GET,POST,UPDATE,DELETE,OPTIONS',
                'Access-Control-Allow-Origin': '*',
                'X-TESTMODE': SessionManager.isDemoMode() ? '1' : ''
            })
        };

        const userName = encodeURIComponent(username);
        const passComplete = encodeURIComponent(password);

        const credentials = `grant_type=password&username=${userName}&password=${passComplete}&clientid=partner`;

        return this.http.post(`${this.baseUrl}/token`, credentials, httpOpts)
            .toPromise().then(
                (response: any) => {

                    const vendorStoresJson = JSON.parse(response.vendorStores);

                    const session = new GiviScanSessionModel({
                        accessToken: response.access_token,
                        refreshToken: response.refresh_token,
                        userId: response.id,
                        vendorStores: vendorStoresJson,
                        user: new CustomerModel(response)
                    });

                    const loggedIn = (session.accessToken != null);
                    if (loggedIn) {

                        if (remember) {

                            const loginModel = new LoginModel({ username, password, remember });
                            // store the credential to be remembered
                            SessionManager.setGiviScanCredentials(loginModel);
                        } else {

                            SessionManager.clearGiviScanSession();
                        }
                    }

                    SessionManager.setGiviScanSession(session);

                    return session;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(errMsg));
                }
            );
    }

    // POST api/ForgotPassword
    forgotPassword(username: string): Promise<any> {

        // Implementation using Promise
        return this.http.post(`${this.baseUrl}/api/ForgotPasswordApi`, { username }, this.getHttpOptions())
            .toPromise();
    }

    //POST: api/admin/account/token/email/create/v1
    generateCustomerTokenWithEmail(customerAccessToken: NewCustomerAccessTokenModel): Promise<any> {

        // Implementation using Promise
        return this.http.post(`${this.baseUrl}/api/admin/account/token/email/create/v1`, customerAccessToken, this.getHttpOptions())
            .toPromise();
    }

    //POST: api/admin/account/token/phone/create/v1
    generateCustomerTokenWithPhone(customerAccessToken: NewCustomerAccessTokenModel): Promise<any> {

        // Implementation using Promise
        return this.http.post(`${this.baseUrl}/api/admin/account/token/phone/create/v1`, customerAccessToken, this.getHttpOptions())
            .toPromise();
    }

    // Headers del POST /token. X-COUNTRY solo si se conoce el país: sin header el API
    // cae a CR para cuentas nuevas y no toca las existentes.
    private _tokenHeaders(country?: Country): { [name: string]: string } {

        const headers: { [name: string]: string } = {
            'Cache-Control': 'no-cache',
            'Content-Type': 'application/x-www-form-urlencoded',
            'Access-Control-Allow-Headers': 'Content-Type',
            'Access-Control-Allow-Methods': 'GET,POST,UPDATE,DELETE,OPTIONS',
            'Access-Control-Allow-Origin': '*',
            'X-TESTMODE': SessionManager.isDemoMode() ? '1' : ''
        };

        if (country) {
            headers['X-COUNTRY'] = String(country);
        }

        return headers;
    }
}
